import React, { useState, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { 
  X, 
  RotateCcw, 
  MessageSquare, 
  UserCheck, 
  Calendar, 
  CheckCircle2, 
  AlertCircle,
  FileText
} from 'lucide-react';

const WITHDRAW_REASONS = [
  '회사 잔류 및 주요 프로젝트 계속 참여 결정',
  '부서 재배치 및 직무 조정 합의',
  '처우 개선 및 유연근무 지원 합의',
  '개인/가사 사정 해소 및 잔류 희망',
  '직무 스트레스 완화 및 휴직/휴가 지원 합의',
  '기타 직접 입력'
];

const ResignationWithdrawModal = ({ contract, isOpen, onClose, onWithdrawnSuccess }) => {
  const { currentUser, withdrawResignation } = useContext(AppContext);

  if (!isOpen || !contract) return null;

  const isApplicant = currentUser?.UserID === contract.UserID;
  const [counselingDate, setCounselingDate] = useState(new Date().toISOString().slice(0, 10));
  const [counselorName, setCounselorName] = useState(currentUser?.Name || '인사팀');
  const [reasonCategory, setReasonCategory] = useState(WITHDRAW_REASONS[0]);
  const [customReason, setCustomReason] = useState('');
  const [interviewNotes, setInterviewNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      const finalReason = reasonCategory === '기타 직접 입력' 
        ? customReason 
        : `${reasonCategory}${customReason ? ` (${customReason})` : ''}`;

      const withdrawalPayload = {
        counselingDate,
        counselorName: counselorName.trim(),
        counselorId: currentUser?.UserID,
        counselorRole: currentUser?.Position || currentUser?.Role,
        reason: finalReason,
        interviewNotes: interviewNotes.trim() || '임직원 면담을 거쳐 사직 의사를 원만히 철회하고 회사에 계속 근무하기로 합의함.',
        withdrawnAt: new Date().toISOString(),
        withdrawnBy: `${currentUser?.Name} (${currentUser?.Position || currentUser?.Role})`
      };

      const ok = await withdrawResignation(contract.ContractID, currentUser, withdrawalPayload);
      if (ok) {
        alert('퇴사 신청이 정상적으로 취하되었습니다.\n작성된 상담 및 면담 일지는 히스토리에 영구 보관됩니다.');
        if (onWithdrawnSuccess) onWithdrawnSuccess();
        onClose();
      }
    } catch (err) {
      alert('퇴사 취하 처리 중 오류가 발생했습니다: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-xs flex items-center justify-center p-2 md:p-4 overflow-y-auto text-left">
      <div className="bg-white rounded-3xl max-w-xl w-full max-h-[92vh] flex flex-col shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center text-white shadow-md">
              <RotateCcw className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base md:text-lg font-bold">퇴사 신청 취하 및 면담 일지 등록</h2>
                <span className="text-[10px] bg-white/20 text-white border border-white/30 px-2 py-0.5 rounded-full font-bold">
                  Withdrawal & Counseling
                </span>
              </div>
              <p className="text-xs text-white/90 mt-0.5">사직 의사를 철회하고 사원과의 면담/상담 내용을 감사 히스토리로 기록합니다.</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-white/80 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 md:p-6 space-y-4 text-xs text-slate-800">
          
          {/* Target Resignation Info */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-900">취하 대상 사직원</span>
              <span className="text-[10px] bg-rose-50 text-rose-700 border border-rose-200 px-2 py-0.5 rounded-md font-bold">
                DOC-{contract.ContractID}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px] pt-1 border-t border-slate-200 text-slate-600">
              <div>신청자: <strong className="text-slate-900">{contract.UserName}</strong> ({contract.Department} {contract.Position})</div>
              <div>퇴사 예정일: <strong className="text-rose-600 font-bold">{contract.ResignationDate || contract.ContractEndDate || '-'}</strong></div>
            </div>
          </div>

          {/* Counseling & Interview Log Fields */}
          <div className="bg-amber-50/50 border border-amber-200/80 rounded-2xl p-4 space-y-3.5">
            <span className="text-xs font-bold text-amber-900 block border-b border-amber-200 pb-2 flex items-center gap-1.5">
              <MessageSquare className="w-4 h-4 text-amber-700" />
              면담 및 취하 상담 정보 입력
            </span>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-bold text-slate-700 block mb-1">상담 / 면담 일자 *</label>
                <input
                  type="date"
                  required
                  value={counselingDate}
                  onChange={e => setCounselingDate(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl font-bold text-slate-900 focus:outline-none focus:border-amber-600"
                />
              </div>

              <div>
                <label className="text-[11px] font-bold text-slate-700 block mb-1">상담자 / 면담 진행자 *</label>
                <input
                  type="text"
                  required
                  value={counselorName}
                  onChange={e => setCounselorName(e.target.value)}
                  placeholder="예: 이용관 대표이사, 이상희 인사담당"
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl font-medium text-slate-800 focus:outline-none focus:border-amber-600"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-700 block mb-1">퇴사 취하 및 잔류 결정 사유 *</label>
              <select
                value={reasonCategory}
                onChange={e => setReasonCategory(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl font-bold text-slate-900 focus:outline-none focus:border-amber-600"
              >
                {WITHDRAW_REASONS.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            {reasonCategory === '기타 직접 입력' && (
              <div>
                <label className="text-[11px] font-bold text-slate-700 block mb-1">직접 입력 사유 *</label>
                <input
                  type="text"
                  required
                  value={customReason}
                  onChange={e => setCustomReason(e.target.value)}
                  placeholder="상세 취하 사유를 입력하세요."
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:border-amber-600 font-medium"
                />
              </div>
            )}

            <div>
              <label className="text-[11px] font-bold text-slate-700 block mb-1">
                퇴사자와의 상담 및 면담 상세 내용 (히스토리 영구 보관) *
              </label>
              <textarea
                rows={3}
                required
                value={interviewNotes}
                onChange={e => setInterviewNotes(e.target.value)}
                placeholder="면담 시 논의된 주요 내용, 잔류 결정 사유, 지원/개선 약속 사항 등을 상세히 기재해 주세요."
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-slate-900 focus:outline-none focus:border-amber-600 resize-none font-medium"
              />
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3 text-[11px] text-slate-600 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <span>
              취하가 완료되면 해당 직원의 사직원은 <strong>[퇴사 취하 완료]</strong> 상태로 전환되며, 임직원 퇴사 신청 버튼이 다시 활성화되어 향후 필요 시 재신청할 수 있습니다.
            </span>
          </div>

          {/* Action Buttons */}
          <div className="pt-2 flex items-center justify-end gap-2 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl border border-slate-300 bg-white hover:bg-slate-100 font-bold text-slate-700 cursor-pointer"
            >
              닫기
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white font-bold shadow-md cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <CheckCircle2 className="w-4 h-4" />
              <span>{isSubmitting ? '취하 처리 중...' : '퇴사 취하 및 상담일지 저장'}</span>
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default ResignationWithdrawModal;
